import type { GetChangeRequestsByApprovalResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetChangeRequestsByApprovalQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetChangeRequestsByApprovalDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback } from 'react';
import { createLazyQueryHook } from 'src/utils';

type ChangeRequestsByApprovalArgs = { approvalId: string };

/**
 * Imperative (lazy) read of the PENDING change requests touching a given
 * approval config — RSP-6099. ApprovalForm calls this on demand before an
 * edit/delete and awaits a FRESH result.
 *
 * Built on the shared `createLazyQueryHook` factory (RSP-6655): the raw Apollo
 * `useLazyQuery` now lives inside the factory, not here. The factory's tRPC
 * branch issues a direct (uncached) client query — equivalent to the previous
 * `staleTime: 0`; the GraphQL branch uses `network-only`.
 *
 * NOTE (RSP-6655 behaviour change): the factory reports fetch errors via a
 * global error notification. Its tRPC branch resolves with empty data, but the
 * legacy Apollo (flag-off) branch awaits `useLazyQuery` unguarded and can
 * reject on a network/GraphQL error. `getChangeRequestsByApproval` therefore
 * wraps the call in try/catch and always returns `[]` on error, never throwing
 * — which is what the consumer wants (a throw would route through ModalForm's
 * mapFormError and render a spurious global error toast on top of the in-flight
 * modal). The factory has already surfaced the failure via the notification.
 */
const useLazyChangeRequestsByApproval = createLazyQueryHook<
  ChangeRequestsByApprovalArgs,
  GetChangeRequestsByApprovalResponseRow[],
  GetChangeRequestsByApprovalQuery
>({
  trpcQuery: (client, { approvalId }) =>
    client.frontend.changeRequest.changeRequestsByApproval.query({
      approvalId,
    }),
  mapTrpcDataToGraphQL: (rows) => ({ change_request: rows }),
  graphqlDocument: GetChangeRequestsByApprovalDocument,
  graphqlFetchPolicy: 'network-only',
});

export const useGetChangeRequestsByApproval = () => {
  const [fetchChangeRequestsByApproval] = useLazyChangeRequestsByApproval();

  const getChangeRequestsByApproval = useCallback(
    async (approvalId: string): Promise<{ Id: string }[]> => {
      try {
        const result = await fetchChangeRequestsByApproval({ approvalId });

        return result.data?.change_request ?? [];
      } catch {
        return [];
      }
    },
    [fetchChangeRequestsByApproval]
  );

  return { getChangeRequestsByApproval };
};
